import { Link } from "react-router-dom";
import Reveal from "../components/Reveal";

const LAST_UPDATED = "23 April 2026";
const COMPANY = "Silly Butterfly";
const EMAIL = "hello@sillybutterfly.com";

const H2 = {
  fontFamily: "var(--font-display)",
  fontSize: "1.2rem",
  fontWeight: 600,
  color: "var(--plum)",
  margin: "0 0 12px",
};

const P = {
  color: "var(--text-secondary)",
  lineHeight: 1.8,
  fontSize: "0.95rem",
  margin: "0 0 14px",
};

const LI = {
  color: "var(--text-secondary)",
  lineHeight: 1.8,
  fontSize: "0.95rem",
  marginBottom: "6px",
  paddingLeft: "4px",
};

const PROSE_SECTION = {
  paddingBottom: "32px",
  marginBottom: "32px",
  borderBottom: "1px solid rgba(212,168,83,0.15)",
};

const PROSE_SECTION_LAST = {
  paddingBottom: 0,
  marginBottom: 0,
};

export default function Privacy() {
  return (
    <main style={{ paddingTop: "var(--nav-h)" }}>
      {/* Content */}
      <section className="sb-section">
        <div className="sb-container" style={{ maxWidth: "980px" }}>
          <Reveal>
            <div style={{
              background: "var(--white)", borderRadius: "var(--radius-lg)",
              padding: "clamp(32px, 5vw, 56px)",
              boxShadow: "var(--shadow-md)",
              border: "1px solid rgba(212,168,83,0.08)",
            }}>

              <div style={PROSE_SECTION}>
                <span style={{ display: "inline-block", fontFamily: "var(--font-body)", fontWeight: 600, fontSize: "0.78rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--gold)", marginBottom: "10px" }}>
                  Legal
                </span>
                <h1 style={{ color: "var(--plum)", margin: "0 0 10px" }}>
                  Privacy Policy
                </h1>
                <p style={{ ...P, marginBottom: 0, fontSize: "0.85rem" }}>
                  Last updated: {LAST_UPDATED}
                </p>
              </div>

              <div style={PROSE_SECTION}>
                <h2 style={H2}>1. Who We Are</h2>
                <p style={P}>{COMPANY} is the data controller responsible for your personal information. We are committed to protecting your privacy and handling your data in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.</p>
                <p style={P}>If you have any questions about how we handle your data, you can contact us at <a href={`mailto:${EMAIL}`} style={{ color: "var(--gold)" }}>{EMAIL}</a>.</p>
              </div>

              <div style={PROSE_SECTION}>
                <h2 style={H2}>2. Information We Collect</h2>
                <p style={P}>We collect personal information in the following ways:</p>
                <p style={{ ...P, marginBottom: "8px" }}><strong style={{ color: "var(--plum)" }}>Information you provide directly:</strong></p>
                <ul style={{ paddingLeft: "20px", margin: "0 0 14px" }}>
                  <li style={LI}>Name, email address, and phone number when you submit a quote or enquiry form</li>
                  <li style={LI}>Event details (date, location, guest numbers, preferences)</li>
                  <li style={LI}>Marketing preferences (whether you opt in to receiving updates)</li>
                  <li style={LI}>Payment information (processed securely by our payment provider — we do not store card details)</li>
                </ul>
                <p style={{ ...P, marginBottom: "8px" }}><strong style={{ color: "var(--plum)" }}>Information collected automatically:</strong></p>
                <ul style={{ paddingLeft: "20px", margin: "0 0 14px" }}>
                  <li style={LI}>Browser type and device information</li>
                  <li style={LI}>Pages visited and time spent on site</li>
                  <li style={LI}>IP address and approximate location</li>
                  <li style={LI}>Referral source (how you found our website)</li>
                </ul>
              </div>

              <div style={PROSE_SECTION}>
                <h2 style={H2}>3. How We Use Your Information</h2>
                <p style={P}>We use your personal information to:</p>
                <ul style={{ paddingLeft: "20px", margin: "0 0 14px" }}>
                  <li style={LI}>Respond to your enquiries and provide you with a quote</li>
                  <li style={LI}>Process and manage your booking</li>
                  <li style={LI}>Send you booking confirmations, reminders, and event updates</li>
                  <li style={LI}>Send marketing communications if you have opted in</li>
                  <li style={LI}>Improve our website and services</li>
                  <li style={LI}>Meet our legal and financial obligations</li>
                </ul>
              </div>

              <div style={PROSE_SECTION}>
                <h2 style={H2}>4. Legal Basis for Processing</h2>
                <p style={P}>Under UK GDPR, we process your personal data on the following legal bases:</p>
                <ul style={{ paddingLeft: "20px", margin: "0 0 14px" }}>
                  <li style={LI}><strong style={{ color: "var(--plum)" }}>Contract:</strong> To fulfil a booking or enquiry you have made with us.</li>
                  <li style={LI}><strong style={{ color: "var(--plum)" }}>Legitimate interests:</strong> To improve our services and communicate about your booking.</li>
                  <li style={LI}><strong style={{ color: "var(--plum)" }}>Consent:</strong> For marketing emails, which you can withdraw at any time.</li>
                  <li style={LI}><strong style={{ color: "var(--plum)" }}>Legal obligation:</strong> To comply with applicable laws and regulations.</li>
                </ul>
              </div>

              <div style={PROSE_SECTION}>
                <h2 style={H2}>5. Sharing Your Information</h2>
                <p style={P}>We do not sell your personal data. We may share it with trusted third parties only where necessary:</p>
                <ul style={{ paddingLeft: "20px", margin: "0 0 14px" }}>
                  <li style={LI}><strong style={{ color: "var(--plum)" }}>Netlify:</strong> Our website and form submissions are hosted via Netlify, which stores submitted form data on our behalf.</li>
                  <li style={LI}><strong style={{ color: "var(--plum)" }}>Email and communication tools:</strong> To send booking confirmations and updates.</li>
                  <li style={LI}><strong style={{ color: "var(--plum)" }}>Analytics providers:</strong> Anonymous usage data to help us improve the site.</li>
                  <li style={LI}><strong style={{ color: "var(--plum)" }}>Legal authorities:</strong> Where required to do so by law.</li>
                </ul>
              </div>

              <div style={PROSE_SECTION}>
                <h2 style={H2}>6. Data Retention</h2>
                <p style={P}>We retain your personal data only for as long as necessary:</p>
                <ul style={{ paddingLeft: "20px", margin: "0 0 14px" }}>
                  <li style={LI}>Booking and transaction records are kept for up to 7 years for legal and accounting purposes.</li>
                  <li style={LI}>Enquiries that did not result in a booking are kept for up to 12 months.</li>
                  <li style={LI}>Marketing preferences are retained until you unsubscribe or request deletion.</li>
                </ul>
              </div>

              <div style={PROSE_SECTION}>
                <h2 style={H2}>7. Your Rights</h2>
                <p style={P}>Under UK GDPR, you have the right to:</p>
                <ul style={{ paddingLeft: "20px", margin: "0 0 14px" }}>
                  <li style={LI}><strong style={{ color: "var(--plum)" }}>Access</strong> the personal data we hold about you.</li>
                  <li style={LI}><strong style={{ color: "var(--plum)" }}>Rectify</strong> any inaccurate or incomplete data.</li>
                  <li style={LI}><strong style={{ color: "var(--plum)" }}>Erase</strong> your data ("right to be forgotten") in certain circumstances.</li>
                  <li style={LI}><strong style={{ color: "var(--plum)" }}>Restrict</strong> or object to how we process your data.</li>
                  <li style={LI}><strong style={{ color: "var(--plum)" }}>Portability</strong> — receive a copy of your data in a machine-readable format.</li>
                  <li style={LI}><strong style={{ color: "var(--plum)" }}>Withdraw consent</strong> for marketing at any time.</li>
                </ul>
                <p style={P}>To exercise any of these rights, please contact us at <a href={`mailto:${EMAIL}`} style={{ color: "var(--gold)" }}>{EMAIL}</a>. We will respond within 30 days.</p>
                <p style={P}>If you are unhappy with how we handle your data, you have the right to lodge a complaint with the Information Commissioner's Office (ICO) at <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" style={{ color: "var(--gold)" }}>ico.org.uk</a>.</p>
              </div>

              <div style={PROSE_SECTION}>
                <h2 style={H2}>8. Cookies</h2>
                <p style={P}>Our website uses cookies to improve your browsing experience. Please see our <Link to="/cookies" style={{ color: "var(--gold)" }}>Cookies Policy</Link> for full details.</p>
              </div>

              <div style={PROSE_SECTION}>
                <h2 style={H2}>9. Changes to This Policy</h2>
                <p style={P}>We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated "last updated" date. We encourage you to review this page periodically.</p>
              </div>

              <div style={PROSE_SECTION_LAST}>
                <h2 style={H2}>10. Contact Us</h2>
                <p style={{ ...P, marginBottom: "6px" }}>For any privacy-related questions or requests, please contact:</p>
                <p style={P}>
                  <strong style={{ color: "var(--plum)" }}>{COMPANY}</strong><br />
                  <a href={`mailto:${EMAIL}`} style={{ color: "var(--gold)" }}>{EMAIL}</a>
                </p>
              </div>

            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
